import { DestroyRef, inject, Injector, Service } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';

const APP_TITLE_KEY = 'WEB_APP_TITLE';

@Service()
export class PageTitleService {
  title = inject(Title);
  translate = inject(TranslateService);

  routeTitleKey: Nullable<string> = null;
  titleOverrides = new Map<DestroyRef, string>();

  constructor() {
    merge(
      this.translate.onLangChange,
      this.translate.onTranslationChange
    ).subscribe(() => {
      this.applyTitle();
    });
  }

  setRouteTitleKey = (key: Nullable<string>) => {
    this.routeTitleKey = key;
    this.applyTitle();
  };

  setTitleOverride = (owner: DestroyRef, key: string) => {
    if (!this.titleOverrides.has(owner)) {
      owner.onDestroy(() => {
        this.titleOverrides.delete(owner);
        this.applyTitle();
      });
    }

    this.titleOverrides.set(owner, key);
    this.applyTitle();
  };

  applyTitle = () => {
    const appTitle: string = this.translate.instant(APP_TITLE_KEY);

    if (appTitle === APP_TITLE_KEY) {
      return;
    }

    const pageTitleKey =
      [...this.titleOverrides.values()].at(-1) ?? this.routeTitleKey;

    this.title.setTitle(
      pageTitleKey
        ? `${appTitle} > ${this.translate.instant(pageTitleKey)}`
        : appTitle
    );
  };
}

@Service()
export class PageTitleStrategy extends TitleStrategy {
  injector = inject(Injector);

  override updateTitle(snapshot: RouterStateSnapshot) {
    this.injector
      .get(PageTitleService)
      .setRouteTitleKey(this.buildTitle(snapshot) ?? null);
  }
}
